import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'

const OPTION_TYPE = {
  default: 'default_settings',
  space: 'community_space'
}

const BADGE_LABEL_BY_SETTINGS_TYPE = {
  default: 'Default',
  customized: 'Customized'
}

const SPACES_HEADER_CLASS = 'spaces-header text-11 font-medium uppercase tracking-wider text-seasalt-500 px-16 pt-12 pb-4'

const defer = callback => setTimeout(callback, 0)

const createElement = (tag, className, textContent) => {
  const element = document.createElement(tag)
  if (className) element.className = className
  if (textContent !== undefined) element.textContent = textContent
  return element
}

export default class extends Controller {
  static values = {
    spacesHeader: { type: String, default: 'Spaces' }
  }

  connect() {
    this.select = new TomSelect(this.element, this.tomSelectConfig())
    this.refreshDropdown()
  }

  disconnect() {
    this.select?.destroy()
  }

  tomSelectConfig() {
    return {
      plugins: { dropdown_input: {} },
      labelField: 'label',
      valueField: 'value',
      dataAttr: 'data',
      searchField: 'label',
      allowEmptyOption: true,
      hidePlaceholder: false,
      hideSelected: false,
      maxOptions: null,
      closeAfterSelect: true,
      controlInput: '<input>',
      render: {
        item: data => this.renderItem(data),
        option: data => this.renderOption(data),
        no_results: () => '<div class="no-results px-16 py-10 text-14 text-seasalt-500">No matches</div>'
      },
      onDropdownOpen: () => defer(() => this.refreshDropdown()),
      onType: () => defer(() => this.refreshDropdown()),
      onChange: () => defer(() => this.updateTickIcons())
    }
  }

  renderItem(data) {
    const wrapper = createElement('div', 'item')
    wrapper.dataset.value = data.value
    wrapper.appendChild(this.buildRowInfo(data))
    return wrapper
  }

  renderOption(data) {
    const wrapper = createElement('div', 'flex items-center gap-8 w-full')
    wrapper.dataset.value = data.value
    wrapper.appendChild(this.buildRowInfo(data))
    wrapper.appendChild(this.buildTick())
    return wrapper
  }

  buildRowInfo(data) {
    const info = createElement('div', 'flex flex-col gap-2 flex-1 min-w-0')
    info.appendChild(this.buildTitleRow(data))
    info.appendChild(createElement('span', 'text-12 text-seasalt-500', this.secondaryText(data)))
    return info
  }

  buildTitleRow(data) {
    const row = createElement('div', 'flex items-center gap-8 flex-wrap')
    row.appendChild(createElement('strong', 'text-14 text-darkblue font-semibold truncate', data.label))

    const badge = this.buildBadge(data)
    if (badge) row.appendChild(badge)

    return row
  }

  buildBadge(data) {
    if (data.type === OPTION_TYPE.default) return null

    const label = BADGE_LABEL_BY_SETTINGS_TYPE[data.data_collection_settings_type]
    if (!label) return null

    return createElement('span', 'status status--sm status--gray shrink-0', label)
  }

  buildTick() {
    const wrapper = createElement('span', 'ml-auto shrink-0 hidden')
    wrapper.appendChild(createElement('i', 'icon-tick text-flameorange text-xl'))
    return wrapper
  }

  secondaryText(data) {
    if (data.type === OPTION_TYPE.default) {
      const count = Number(data.spaces_count ?? 0)
      return `Inherited by ${count} ${count === 1 ? 'space' : 'spaces'}`
    }

    const prospects = Number(data.prospects_count ?? 0)
    const ambassadors = Number(data.ambassadors_count ?? 0)
    return `${prospects} prospects · ${ambassadors} ambassadors`
  }

  refreshDropdown() {
    this.insertSpacesHeader()
    this.updateTickIcons()
  }

  insertSpacesHeader() {
    const firstSpaceNode = this.findFirstSpaceNode()
    if (!firstSpaceNode) return

    const previous = firstSpaceNode.previousElementSibling
    if (previous?.classList.contains('spaces-header')) return

    const header = createElement('div', SPACES_HEADER_CLASS, this.spacesHeaderValue)
    firstSpaceNode.parentNode.insertBefore(header, firstSpaceNode)
  }

  updateTickIcons() {
    const dropdownContent = this.select?.dropdown_content
    if (!dropdownContent) return

    const currentValue = this.select.getValue()

    this.optionNodes().forEach(node => {
      const tick = node.querySelector('.icon-tick')?.parentElement
      tick?.classList.toggle('hidden', node.dataset.value !== currentValue)
    })
  }

  findFirstSpaceNode() {
    return this.optionNodes().find(node => this.optionType(node) === OPTION_TYPE.space)
  }

  optionNodes() {
    const dropdownContent = this.select?.dropdown_content
    if (!dropdownContent) return []

    return Array.from(dropdownContent.childNodes).filter(node => node.dataset?.value !== undefined)
  }

  optionType(node) {
    return this.select.options[node.dataset.value]?.type ?? null
  }
}
